import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Country } from "../../../../../../shared/constants/Constant";
import { useFirestore } from "reactfire";
import { Line } from "rc-progress";
import {
  SubsectionStatus,
  WorkshopProgress,
} from "../../../../../../contexts/user-context/UserContext";
import { WorkshopSection } from "../../../../../../contexts/workshop-context/WorkshopContext";

interface Member {
  memberId: string;
  fullName: string;
  email: string;
  contact: string;
  university: string;
  country: Country;
  pastExperience?: string;
  workshopProgress?: WorkshopProgress[];
  active: boolean;
}

const initMember: Member = {
  memberId: "",
  fullName: "",
  email: "",
  contact: "",
  university: "",
  country: Country.SRI_LANKA,
  workshopProgress: [],
  active: false,
};

interface Workshop {
  id: string;
  title: string;
  description: string;
  createdAt: Date;
  country: Country;
  orderNo: number;
  thumbnail: string;
  points: number;
  workshopSections: any[];
}

const MemberProfileComponent = () => {
  const store = useFirestore();

  const [member, setMember] = useState(initMember);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);

  const [workshopProgress, setWorkshopProgress] = useState<{
    completion: number;
    points: number;
    workshops: { name: string; progress: number; points: number }[];
  }>({ completion: 0, points: 0, workshops: [] });

  useEffect(() => {
    let marks: { id: string; count: number; status: SubsectionStatus }[] = [];

    member.workshopProgress?.forEach((wksProgress) => {
      const [workshop] = workshops.filter(
        (w) => w.id === wksProgress.workshopId
      );
      const u_subsectons = wksProgress.subsectionsProgress.filter(
        (v, i, a) => a.findIndex((t) => t.subsectionId === v.subsectionId) === i
      );
      const count = u_subsectons.filter(
        (s) =>
          s.status === SubsectionStatus.DONE &&
          workshop?.workshopSections.filter((ww) => ww.id === s.subsectionId)
            .length > 0
      ).length;
      marks.push({
        id: wksProgress.workshopId,
        count: count,
        status: wksProgress.status,
      });
    });

    let overall = 0;

    let memberPoints: number = 0;
    const w_list: { name: string; progress: number; points: number }[] = [];
    marks?.forEach((m) => {
      const [workshop] = workshops.filter((w) => w.id === m.id);
      if (workshop) {
        const points = workshop.points;
        const totalSubsectionCount = workshop.workshopSections.length;
        const completedSubsectionCount = m.count || 0;

        if (completedSubsectionCount > 0) {
          memberPoints += Math.round(
            (completedSubsectionCount / totalSubsectionCount) * points
          );
          w_list.push({
            name: workshop.title,
            progress: Math.round(
              (completedSubsectionCount / totalSubsectionCount) * 100
            ),
            points: Math.round(
              (completedSubsectionCount / totalSubsectionCount) * points
            ),
          });
        }
      }
    });

    let workshopTotalPoints: number = 0;
    workshops
      .map((w) => w.points)
      .forEach((p) => {
        workshopTotalPoints = +workshopTotalPoints + +p;
      });
    overall = Math.round((memberPoints / workshopTotalPoints) * 100);

    if (overall && memberPoints)
      setWorkshopProgress({
        completion: overall,
        points: memberPoints,
        workshops: w_list,
      });
  }, [workshops, member.workshopProgress]);

  useEffect(() => {
    if (member.memberId === "") {
      const currentPath = window.location.pathname;
      const tId = currentPath.substr(currentPath.indexOf("members/") + 8);
      setMember((ps) => ({ ...ps, memberId: tId }));
    }
  }, [member.memberId]);

  useEffect(() => {
    setWorkshops([]);
    let unset: any;
    if (unset) unset();

    if (member.country) {
      unset = store
        .collection("workshops")
        .where("active", "==", true)
        .where("country", "==", member.country)
        .onSnapshot({}, (snapshot) => {
          snapshot.docChanges().forEach((change) => {
            if (change.type === "added") {
              setWorkshops((ps) => {
                ps.push({
                  id: change.doc.id,
                  title: change.doc.data().title,
                  description: change.doc.data().description,
                  createdAt: change.doc.data().createdAt,
                  country: change.doc.data().country,
                  orderNo: change.doc.data().orderNo,
                  thumbnail: change.doc.data().thumbnail,
                  points: change.doc.data().points,
                  workshopSections: (change.doc.data()
                    .workshopSections as WorkshopSection[])?.filter(
                    (s) => !s.archived
                  ),
                });
                return Object.assign([], ps);
              });
            }
            if (change.type === "modified") {
              setWorkshops((ps) => {
                const modified = ps.map((a) => {
                  if (a.id === change.doc.id) {
                    return {
                      id: change.doc.id,
                      title: change.doc.data().title,
                      description: change.doc.data().description,
                      createdAt: change.doc.data().createdAt,
                      country: change.doc.data().country,
                      orderNo: change.doc.data().orderNo,
                      thumbnail: change.doc.data().thumbnail,
                      points: change.doc.data().points,
                      workshopSections: (change.doc.data()
                        .workshopSections as WorkshopSection[])?.filter(
                        (s) => !s.archived
                      ),
                    };
                  } else {
                    return a;
                  }
                });
                return Object.assign([], modified);
              });
            }
            if (change.type === "removed") {
              setWorkshops((ps) => {
                const removed = ps.filter((a) => a.id !== change.doc.id);
                return Object.assign([], removed);
              });
            }
          });
        });
    }
    return unset;
  }, [store, member.country]);

  useEffect(() => {
    if (member.memberId && member.memberId !== "") {
      return store
        .collection("members")
        .doc(member.memberId)
        .onSnapshot({}, (change1) => {
          const data: any = change1.data();
          setMember((ps) => ({
            ...ps,
            ...data,
            active: data.active === undefined ? true : data.active,
            contact:
              data && data.contact
                ? data.contact
                : data.contactNumber
                ? data.contactNumber
                : "",
          }));
        });
    }
  }, [member.memberId, store]);

  const getCountryString = (c: Country | undefined) => {
    switch (c) {
      case Country.BOSNIA:
        return "Bosnia and Herzegovina";
      case Country.JAMAICA:
        return "Jamaica";
      case Country.MALAYSIA:
        return "Malaysia";
      case Country.INDONESIA:
        return "Indonesia";
      case Country.SRI_LANKA:
        return "Sri Lanka";
      case Country.INDIA:
        return "India";
      default:
        return "";
    }
  };

  return (
    <Container fluid className="team-profile-container">
      <Row>
        <Col xs={6}>
          <div className="mb-3">
            <label className="font-weight-bold normal-label">Name :</label>
            <label className="pl-3 normal-label">{member.fullName}</label>
          </div>
          {member.email ? (
            <div className="mb-3">
              <label className="font-weight-bold normal-label">Email :</label>
              <label className="pl-3 normal-label">{member.email}</label>
            </div>
          ) : (
            <div />
          )}
          <div className="mb-3">
            <label className="font-weight-bold normal-label">Country :</label>
            <label className="pl-3 normal-label">
              {getCountryString(member.country)}
            </label>
          </div>
          <div className="mb-3">
            <label className="font-weight-bold normal-label">Status :</label>
            {member.active ? (
              <label className={"pl-3 normal-label active"}>Active</label>
            ) : (
              <label className={"pl-3 normal-label inactive"}>Inactive</label>
            )}
            {/*{member.active ? (*/}
            {/*  <button className="ml-3 btn-deactivate">Deactivate</button>*/}
            {/*) : (*/}
            {/*  <button className="ml-3 btn-activate">Activate</button>*/}
            {/*)}*/}
          </div>
        </Col>
        <Col xs={6} className="text-right">
          <div className="mb-3">
            <label className="font-weight-bold sized-label">
              Workshop Completion :
            </label>
            <label className="pl-3 percentage">
              {workshopProgress.completion}%
            </label>
          </div>
          <div className="mb-3">
            <label className="font-weight-bold sized-label">Points :</label>
            <label className="pl-3 sized-label font-weight-bold">
              {workshopProgress.points}
            </label>
          </div>
        </Col>
      </Row>
      {workshopProgress &&
      workshopProgress.workshops &&
      workshopProgress.workshops.length ? (
        <Row className="mt-5">
          <Col xs={12} className="d-flex">
            <div className="flex-title">
              <label
                className="font-weight-bold normal-label"
                style={{ paddingTop: "23px" }}
              >
                Workshops taken :
              </label>
            </div>
            <div className="flex-content" style={{ flex: "1" }}>
              <table className="tbl-team-members ml-3 w-100">
                <tbody>
                  {workshopProgress.workshops.map((w, index) => (
                    <tr key={index}>
                      <td
                        style={{
                          width: "30%",
                          verticalAlign: "top",
                          // color: "#9e9e9e",
                          paddingTop: "1.4rem",
                          paddingBottom: "1.4rem",
                        }}
                      >
                        {w.name}
                      </td>
                      <td
                        style={{ width: "50%", verticalAlign: "top" }}
                        className="py-4"
                      >
                        <div className="xstudent-progress-bar">
                          <label className="pcntg" style={{ fontSize: "14px" }}>
                            {w.progress}%
                          </label>
                          <div>
                            <Line
                              percent={w.progress}
                              strokeWidth={1}
                              trailWidth={1}
                              strokeColor="#2ecc71"
                              trailColor="#f2f2f2"
                            />
                          </div>
                        </div>
                      </td>
                      <td
                        style={{
                          width: "40%",
                          verticalAlign: "top",
                          fontSize: "20px",
                          fontWeight: "bold",
                          paddingLeft: "40px",
                          // color: "#9e9e9e",
                          paddingTop: "1.2rem",
                          paddingBottom: "1.4rem",
                        }}
                      >
                        Points : {w.points}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Col>
        </Row>
      ) : (
        <></>
      )}
    </Container>
  );
};

export default MemberProfileComponent;
